import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
  ValueGetterParams,
} from 'ag-grid-community';
import { BehaviorSubject, debounceTime, of, Subject, Subscription } from 'rxjs';
import { downloadBlob, noRowsOverlayMessage } from 'src/app/_helpers/utils';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { RoleService } from 'src/app/_services/role.service';
import {
  cancellationFlowToDisplayElement,
  claimToDisplayElement,
  DisplayElement,
  displayElementToCsvRow,
  flowGroupKey,
  insuranceFlowToDisplayElement,
  keepFlowAtHead,
  TASKS_CSV_HEADERS,
  withRunningCancellationBadge,
} from 'src/app/_types/util.types';
import {
  CancellationFlowStatus,
  getCancellationFlowStatusClass,
} from 'src/app/enums/cancellation/cancellation-flow-status.enum';
import { getClaimStatusLabel } from 'src/app/enums/claim/claim-status.enum';
import {
  getInsuranceFlowLabel,
  getInsuranceFlowStatusClass,
} from 'src/app/enums/insurance-flow/insurance-flow-status.enum';
import {
  getInsuranceTypeIcon,
  getInsuranceTypeLabel,
  InsuranceType,
} from 'src/app/enums/insurance-type.enum';
import { getLeadStatusLabel, LeadStatus } from 'src/app/enums/lead-status.enum';
import {
  getLeadStatusByProgression,
  getLeadTypeClass,
  getLeadTypeLabel,
  LeadType,
} from 'src/app/enums/lead-type.enum';
import {
  getPolicyOriginLabel,
  getPolicyStatusLabel,
  PolicyStatus,
} from 'src/app/enums/proposal.enums';
import { Roles } from 'src/app/enums/roles.enum';
import {
  getSortElementLabel,
  getSortMethod,
  LeadSortElement,
  ProposalSortElement,
} from 'src/app/enums/task-sort.enums';
import { Globals, toUTCWithoutChangingTime } from 'src/app/globals';
import { TaskDetailsComponent } from 'src/app/proposals/task-details/task-details.component';
import { GridCopyButtonComponent } from 'src/app/shared/components/grid-copy-button/grid-copy-button.component';
import { environment } from 'src/environments/environment';

import { IconsModule } from '../../icons/icons.module';
import { IconRendererComponent } from '../ag-grid-renderers/icon-renderer/icon-renderer.component';
import { StackedDateRendererComponent } from '../ag-grid-renderers/stacked-date-renderer/stacked-date-renderer.component';
import { StackedTextRendererComponent } from '../ag-grid-renderers/stacked-text-renderer/stacked-text-renderer.component';
import { CancellationDetailsModalComponent } from '../cancellation-details/cancellation-details-modal.component';
import { ToggleComponent } from '../toggle/toggle.component';
import { FlowCollapseButtonComponent } from './flow-collapse-button/flow-collapse-button.component';

@Component({
  selector: 'app-shared-tasks-list',
  templateUrl: './tasks-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TaskDetailsComponent,
    CancellationDetailsModalComponent,
    AgGridModule,
    FormsModule,
    ReactiveFormsModule,
    ToggleComponent,
    IconsModule,
  ],
})
export class TasksListComponent implements OnInit, OnDestroy {
  @Input()
  leadsOnly: boolean = false;

  private _endDate: Date | null = null;

  get endDate() {
    return this._endDate;
  }

  @Input()
  set endDate(value: Date | null) {
    this._endDate = value;
    this.simpleListReset();
  }

  @Input() startDate: Date | null = null;

  DATE_FORMAT = environment.DATE_FORMAT;

  all_List: DisplayElement[] = [];

  displayedList: DisplayElement[] = [];

  context: {
    componentParent: TasksListComponent;
    isFlowGroupExpandable: (row: DisplayElement) => boolean;
    isFlowGroupExpanded: (displayId: string) => boolean;
  };

  gridApi!: GridApi;

  columnApi!: ColumnApi;

  @Output()
  loaded = new EventEmitter<number>();

  @ViewChild('taskDetailsModal') taskDetailsModal: TaskDetailsComponent;

  columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'collapse',
      cellClass: 'actionDot',
      cellRenderer: FlowCollapseButtonComponent,
      width: 50,
      resizable: false,
      sortable: false,
      filter: false,
    },
    {
      headerName: $localize`ID`,
      filter: 'agTextColumnFilter',
      sortable: true,
      field: 'displayId',
      cellRenderer: GridCopyButtonComponent,
      colId: LeadSortElement.ID,
    },
    {
      headerName: $localize`N° police`,
      filter: 'agTextColumnFilter',
      cellClass: 'bold-text-cell',
      sortable: true,
      minWidth: 160,
      colId: 'policyId',
      flex: 1,
      valueGetter: (data) => data.data.policyId,
    },
    {
      headerName: $localize`Client`,
      field: 'customerName',
      filter: 'agTextColumnFilter',
      cellClass: 'bold-text-cell',
      sortable: true,
      minWidth: 160,
      colId: LeadSortElement.CUSTOMER,
    },
    {
      headerName: $localize`Email client`,
      field: 'customerEmail',
      hide: true,
    },
    {
      headerName: $localize`N° téléphone client`,
      field: 'customerPhone',
      hide: true,
    },
    {
      headerName: $localize`Agent`,
      field: 'agentName',
      hide: true,
    },
    {
      headerName: $localize`Partenaire`,
      field: 'partnerName',
      filter: 'agTextColumnFilter',
      cellClass: 'stacked-text',
      sortable: true,
      cellRenderer: StackedTextRendererComponent,
      cellRendererParams: {
        topElement: 'partnerName',
        bottomElement: 'agentName',
        dangerBadgeIfEmpty: true,
        emptyText: $localize`Non assigné`,
      },
      minWidth: 163,
      colId: LeadSortElement.PARTNER,
    },
    {
      headerName: $localize`Type`,
      field: 'insuranceType',
      valueGetter: (params: ValueGetterParams) =>
        getInsuranceTypeLabel(params.data.insuranceType),
      cellClass: 'icon-cell',
      cellRenderer: IconRendererComponent,
      cellRendererParams: {
        iconGetter: getInsuranceTypeIcon,
        iconNameKey: 'insuranceType',
        iconTextKey: 'type',
      },
      minWidth: 245,
      colId: LeadSortElement.TYPE,
    },
    {
      headerName: $localize`Affaire`,
      field: 'type',
      cellRenderer: (params: ValueGetterParams) =>
        `<span class="lty-badge ${getLeadTypeClass(params.data.displayedLeadType ?? params.data.leadType)}">${getLeadTypeLabel(params.data.displayedLeadType ?? params.data.leadType)}</span>`,
      valueGetter: (params: ValueGetterParams) =>
        getLeadTypeLabel(params.data.displayedLeadType ?? params.data.leadType),
      minWidth: 268,
      colId: LeadSortElement.TASK,
    },
    {
      headerName: $localize`Date de création`,
      cellClass: 'stacked-text',
      field: 'creationDate',
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
        new Date(nodeA.data.creationDate).getTime() -
        new Date(nodeB.data.creationDate).getTime(),
      valueGetter: (params: ValueGetterParams) =>
        toUTCWithoutChangingTime(params.data.creationDate)
          .tz('Europe/Zurich')
          .format('DD.MM.yyyy HH:mm'),
      cellRenderer: StackedDateRendererComponent,
      cellRendererParams: {
        dateKey: 'creationDate',
      },
      minWidth: 152,
      colId: LeadSortElement.CREATION_DATE,
    },
    {
      headerName: $localize`Statut`,
      field: 'state',
      // The cell shows more than `state` (the running cancellation's badge):
      // redraw it whenever its row's data is swapped in place.
      equals: () => false,
      cellRenderer: (params: ValueGetterParams) => {
        if (params.data.leadType === LeadType.CLAIM) {
          return `<span class="lty-badge">${getClaimStatusLabel(
            params.data.status
          )}</span>`;
        }
        // A cancellation has statuses of its own, already read out on the row.
        if (params.data.leadType === LeadType.CANCELLATION) {
          return `<span class="lty-badge ${getCancellationFlowStatusClass(
            params.data.status as CancellationFlowStatus
          )}">${params.data.state}</span>`;
        }
        return withRunningCancellationBadge(
          `<span class="lty-badge ${getInsuranceFlowStatusClass(params.data.status)}">${getInsuranceFlowLabel(params.data.status)}</span>`,
          params.data.runningCancellationStatus
        );
      },
      colId: LeadSortElement.STATE,
    },
    {
      headerName: $localize`Màj`,
      cellClass: 'stacked-text',
      field: 'lastUpdateDate',
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) =>
        new Date(nodeA.data.lastUpdateDate).getTime() -
        new Date(nodeB.data.lastUpdateDate).getTime(),
      valueGetter: (params: ValueGetterParams) =>
        params.data.lastUpdateDate
          ? toUTCWithoutChangingTime(params.data.lastUpdateDate)
              .tz('Europe/Zurich')
              .format('DD.MM.yyyy HH:mm')
          : '',
      cellRenderer: StackedDateRendererComponent,
      cellRendererParams: {
        dateKey: 'lastUpdateDate',
      },
      minWidth: 152,
      colId: LeadSortElement.UPDATE_DATE,
    },
    {
      headerName: $localize`Chargé de clientèle`,
      field: 'customerSupportName',
      cellClass: 'simple-text-cell',
      colId: LeadSortElement.CUSTOMER_SUPPORT,
    },
    {
      headerName: $localize`Adresse`,
      field: 'address',
      hide: true,
    },
    {
      headerName: $localize`Compagnie d'assurance`,
      field: 'insuranceCompany',
      hide: true,
    },
    {
      headerName: $localize`Produit`,
      field: 'subcategory',
      hide: true,
    },
    {
      headerName: $localize`Couvertures`,
      field: 'coverages',
      hide: true,
    },
    {
      headerName: $localize`Date de début`,
      field: 'startDate',
      hide: true,
    },
    {
      headerName: $localize`Date de fin`,
      field: 'endDate',
      hide: true,
    },
    {
      headerName: $localize`Prime`,
      field: 'premiumAmount',
      hide: true,
    },
    {
      headerName: $localize`Prime annuelle`,
      field: 'totalAmount',
      hide: true,
    },
    {
      headerName: $localize`Périodicité`,
      field: 'periodicity',
      hide: true,
    },
    {
      headerName: $localize`Commission`,
      field: 'commission',
      hide: true,
    },
  ];

  public defaultColDef: ColDef = {
    filter: true,
    resizable: true,
    autoHeight: true,
    sortable: false,
    flex: 1,
  };

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  adminId!: string;

  constructor(
    private globals: Globals,
    private router: Router,
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly roleService: RoleService
  ) {
    this.context = {
      componentParent: this,
      isFlowGroupExpandable: (row: DisplayElement) =>
        this.isFlowGroupExpandable(row),
      isFlowGroupExpanded: (displayId: string) =>
        this.expandedFlowGroupIds.has(displayId),
    };
    if (localStorage.getItem('user')) {
      this.adminId = JSON.parse(localStorage.getItem('user')!).adminId;
    }
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    this.roleService
      .hasWritePermission(Roles.PROPOSALS)
      .subscribe((canEdit) => {
        this.canEdit = canEdit;
      });
    this.searchStringSubscription = this.searchStringControl.valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.simpleListReset();
      });
  }

  ngOnDestroy(): void {
    this.searchStringSubscription.unsubscribe();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    this.getList();
  }

  exportCsv() {
    const obs = new Subject();
    this.loadAll();
    const subscription = this.endReachedSubject.subscribe((value) => {
      if (value) {
        this.downloadCsv();
        obs.next(true);
        if (subscription) subscription.unsubscribe();
      }
    });
    return obs;
  }

  /**
   * Downloads CSV file with all elements from mainElementsMapping
   */
  private downloadCsv(): void {
    // Build CSV content
    const csvRows: string[] = [];
    csvRows.push(TASKS_CSV_HEADERS.map((h) => `"${h}"`).join(','));

    // The export lists flows and claims; cancellations have their own tab.
    this.all_List
      .filter((element) => element.leadType !== LeadType.CANCELLATION)
      .forEach((element) => {
        csvRows.push(displayElementToCsvRow(element));
      });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([`\ufeff${csvContent}`], {
      type: 'text/csv;charset=utf-8;',
    });
    downloadBlob(blob, `export.csv`);
  }

  onCellClicked(e: CellClickedEvent): void {
    if (
      e.column.getColId() === 'Action' ||
      e.column.getColId() === 'collapse' ||
      e.column.getColId() === LeadSortElement.ID
    ) {
      return;
    }
    switch (e.data.leadType) {
      case LeadType.CLAIM:
        window.open(
          this.router.serializeUrl(
            this.router.createUrlTree(['/proposal/claim/view', e.data.id])
          ),
          '_blank'
        );
        break;
      case LeadType.POLICY:
      case LeadType.OFFER_ACCEPTED:
      case LeadType.SUBMISSION:
      case LeadType.PROPOSAL:
      case LeadType.INSURANCE:
        if (
          e.data.insuranceType === InsuranceType.CANCELLATION ||
          e.data.insuranceType === InsuranceType.COLLABORATOR_DECLARATION ||
          e.data.insuranceType === InsuranceType.POLICY_UPDATE
        )
          window.open(
            this.router.serializeUrl(
              this.router.createUrlTree(['/proposal/lead/view/flow', e.data.id])
            ),
            '_blank'
          );
        else this.taskDetailsModal.openModal(e.data.id);
        break;
      case LeadType.CANCELLATION:
        this.openCancellationId = e.data.id;
        break;
      default:
        break;
    }
  }

  /** The cancellation open in its sheet, over the list rather than instead of it. */
  openCancellationId: string | null = null;

  canEdit = false;

  getList() {
    if (this.fetching || !this.gridApi) return of(false);
    this.fetching = true;
    const obs = new Subject();
    this.insuranceFlowService
      .getAllTasks({
        ...this.globals.getEntityIdObject(),
        pageCount: this.currentPage + 1,
        pageSize: this.pageSize,
        searchBy: this.searchStringControl.value,
        insuranceType: this.filteredInsuranceType,
        includeClaims: !this.leadsOnly,
        sortBy: this.sortBy,
        ascending: this.ascending,
        startDate: this.startDate,
        endDate: this.endDate,
        includeCancellations: true,
      })
      .subscribe({
        next: (res) => {
          if (
            res.data.flowsList?.length === 0 &&
            res.data.claimsList?.length === 0
          )
            this.endReached = true;

          // Cancellations are grouped under their affaire, so they go with the flows.
          const newFlowElements = [
            ...res.data.flowsList.map(insuranceFlowToDisplayElement),
            ...(res.data.cancellations ?? []).map(
              cancellationFlowToDisplayElement
            ),
          ];
          const newClaimElements = res.data.claimsList.map(
            claimToDisplayElement
          );
          this.mergeElements(newFlowElements, newClaimElements);
          this.currentPage++;
          this.displayedList = this.buildDisplayedList();
          this.gridApi.setGridOption('rowData', this.displayedList);

          this.fetching = false;
          this.loaded.emit(this.displayedList.length);
          obs.next(true);
        },
      });
    return obs;
  }

  searchStringSubscription!: Subscription;

  searchStringControl = new FormControl<string | null>('');

  private _leadStepFilter!: Omit<LeadType.CLAIM, LeadType> | null;

  get leadStepFilter() {
    return this._leadStepFilter;
  }

  @Input()
  set leadStepFilter(value) {
    if (value === this._leadStepFilter) return;
    this._leadStepFilter = value;
    this.StatusValues = [];
    if (value)
      this.StatusValues = getLeadStatusByProgression(value as LeadType);
    if (value === LeadType.POLICY) {
      this.columnDefs.splice(
        this.columnDefs.length - 2,
        0,
        {
          headerName: $localize`Date de renouvellement suggérée`,
          cellClass: 'simple-text-cell',
          field: 'recommendedRenewalDate',
          colId: 'recommendedRenewalDate',
          valueGetter: (params: ValueGetterParams) =>
            params.data.recommendedRenewalDate
              ? toUTCWithoutChangingTime(params.data.recommendedRenewalDate)
                  .tz('Europe/Zurich')
                  .format('DD.MM.yyyy')
              : '',
        },
        {
          headerName: $localize`Provenance`,
          cellClass: 'simple-text-cell',
          field: 'origin',
          colId: 'origin',
          valueGetter: (params: ValueGetterParams) =>
            getPolicyOriginLabel(params.data.origin),
        }
      );
      this.SortElementValues = Object.values(ProposalSortElement);
      if (this.gridApi) this.gridApi.setColumnDefs(this.columnDefs);
    } else {
      this.removeColumnIfExist('recommendedRenewalDate');
      this.removeColumnIfExist('origin');
    }
    this.fullReset();
  }

  private removeColumnIfExist(colId: string) {
    const columnToRemoveIndex = this.columnDefs.findIndex(
      (x) => x.colId === colId
    );
    if (columnToRemoveIndex > -1) {
      this.columnDefs.splice(columnToRemoveIndex, 1);
      this.gridApi.setColumnDefs(this.columnDefs);
    }
  }

  StatusValues!: (LeadStatus | PolicyStatus)[];

  private _filteredStatus: LeadStatus | PolicyStatus | null = null;

  get filteredStatus() {
    return this._filteredStatus;
  }

  set filteredStatus(value) {
    this._filteredStatus = value;
    this.simpleListReset();
  }

  InsuranceTypeValues = Object.values(InsuranceType).filter(
    (x) =>
      x !== InsuranceType.CANCELLATION &&
      x !== InsuranceType.CENTRALISATION &&
      x !== InsuranceType.POLICY_UPDATE
  );

  private _filteredInsuranceType: InsuranceType | null = null;

  get filteredInsuranceType() {
    return this._filteredInsuranceType;
  }

  set filteredInsuranceType(value) {
    this._filteredInsuranceType = value;
    this.simpleListReset();
  }

  private _sortBy: LeadSortElement | ProposalSortElement | null =
    LeadSortElement.CREATION_DATE;

  get sortBy() {
    return this._sortBy;
  }

  set sortBy(value: LeadSortElement | ProposalSortElement | null) {
    this._sortBy = value;
    this.simpleListReset();
  }

  private _ascending: boolean = false;

  get ascending() {
    return this._ascending;
  }

  set ascending(value: boolean) {
    this._ascending = value;
    this.simpleListReset();
  }

  getFilterLeadStatusLabel(value: LeadStatus | PolicyStatus) {
    if (this.leadStepFilter === LeadType.POLICY)
      return getPolicyStatusLabel(value as PolicyStatus);
    return getLeadStatusLabel(value as LeadStatus);
  }

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  noRowsOverlayMessage = noRowsOverlayMessage;

  pageSize = 500;

  currentPage = 0;

  private _endReached = false;

  set endReached(value: boolean) {
    this._endReached = value;
    this.endReachedSubject.next(value);
  }

  get endReached() {
    return this._endReached;
  }

  endReachedSubject = new BehaviorSubject(false);

  fetching = false;

  checkRows() {
    const lastDisplayedRow = this.gridApi.getLastDisplayedRow();
    if (
      !this.endReached &&
      lastDisplayedRow === this.displayedList.length - 1
    ) {
      this.getList();
    }
  }

  simpleListReset() {
    this.currentPage = 0;
    this.all_List = [];
    this.displayedList = [];
    this.flowById = {};
    this.claimById = {};
    this.flowGroupsByDisplayId = {};
    this.expandedFlowGroupIds.clear();
    this.endReached = false;
    this.getList();
  }

  fullReset() {
    this._filteredStatus = null;
    this._filteredInsuranceType = null;
    this._sortBy = null;
    this.searchStringControl.reset('', { emitEvent: false });
    this.simpleListReset();
  }

  loadAll() {
    if (!this.endReached)
      this.getList().subscribe((res) => {
        this.loadAll();
      });
  }

  SortElementValues: (ProposalSortElement | LeadSortElement)[] = Object.values(
    LeadSortElement
  ).filter((x) => x !== LeadSortElement.CANCELLATION_INSURANCE_TYPE);

  getSortElementLabel = getSortElementLabel;

  resetDates() {
    this.startDate = null;
    this.endDate = null;
  }

  flowById: Record<string, DisplayElement> = {};

  claimById: Record<string, DisplayElement> = {};

  flowGroupsByDisplayId: Record<string, DisplayElement[]> = {};

  expandedFlowGroupIds = new Set<string>();

  // Expands/collapses one displayId group with incremental grid transactions.
  toggleFlowGroup(displayId: string): void {
    const group = this.flowGroupsByDisplayId[displayId];
    if (!group || group.length <= 1) {
      return;
    }

    const head = group[0];
    const headIndex = this.displayedList.findIndex((x) => x.id === head.id);
    if (headIndex < 0) {
      return;
    }

    if (this.expandedFlowGroupIds.has(displayId)) {
      this.expandedFlowGroupIds.delete(displayId);
      const rowsToRemove = group
        .slice(1)
        .filter((row) =>
          this.displayedList.some((displayed) => displayed.id === row.id)
        );
      if (rowsToRemove.length === 0) {
        return;
      }
      const rowsToRemoveIds = new Set(rowsToRemove.map((x) => x.id));
      for (let i = this.displayedList.length - 1; i >= 0; i--) {
        if (rowsToRemoveIds.has(this.displayedList[i].id)) {
          this.displayedList.splice(i, 1);
        }
      }
      this.gridApi.applyTransaction({ remove: rowsToRemove });
      return;
    }

    this.expandedFlowGroupIds.add(displayId);
    const rowsToAdd = group
      .slice(1)
      .filter(
        (row) =>
          !this.displayedList.some((displayed) => displayed.id === row.id)
      );
    if (rowsToAdd.length === 0) {
      return;
    }
    this.displayedList.splice(headIndex + 1, 0, ...rowsToAdd);
    this.gridApi.applyTransaction({ add: rowsToAdd, addIndex: headIndex + 1 });
  }

  isFlowGroupExpandable(row: DisplayElement): boolean {
    if (row.leadType === LeadType.CLAIM) {
      return false;
    }
    const group = this.flowGroupsByDisplayId[row.displayId];
    return !!group && group.length > 1 && group[0].id === row.id;
  }

  private mergeElements(
    flowElements: DisplayElement[],
    claimElements: DisplayElement[]
  ): void {
    // Merge by id to avoid duplicates when pages are refetched.
    flowElements.forEach((element) => {
      this.flowById[element.id] = element;
    });
    claimElements.forEach((element) => {
      this.claimById[element.id] = element;
    });
    this.rebuildFlowGroups();
    this.all_List = [
      ...Object.values(this.flowById),
      ...Object.values(this.claimById),
    ];
  }

  private rebuildFlowGroups(): void {
    // Recompute groups while preserving user expansion state for existing groups.
    const previousGroupIds = new Set(Object.keys(this.flowGroupsByDisplayId));
    const previousExpanded = new Set(this.expandedFlowGroupIds);
    this.flowGroupsByDisplayId = {};
    Object.values(this.flowById).forEach((element) => {
      const groupId = flowGroupKey(element);
      if (!this.flowGroupsByDisplayId[groupId]) {
        this.flowGroupsByDisplayId[groupId] = [];
      }
      this.flowGroupsByDisplayId[groupId].push(element);
    });

    Object.keys(this.flowGroupsByDisplayId).forEach((groupId) => {
      this.flowGroupsByDisplayId[groupId] = this.sortFlowGroup(
        this.flowGroupsByDisplayId[groupId]
      );
    });

    this.expandedFlowGroupIds = new Set(
      Object.keys(this.flowGroupsByDisplayId).filter(
        (groupId) =>
          !previousGroupIds.has(groupId) || previousExpanded.has(groupId)
      )
    );
  }

  private sortFlowGroup(group: DisplayElement[]): DisplayElement[] {
    // Show the latest row from the selected lead progression as the group head,
    // unless keepFlowAtHead finds an active policy to put ahead of it.
    const sortedByRecency = [...group].sort(
      (a, b) => this.getElementTimestamp(b) - this.getElementTimestamp(a)
    );
    if (this.leadStepFilter) {
      const matchingProgressionIndex = sortedByRecency.findIndex(
        (element) => element.leadType === this.leadStepFilter
      );
      if (matchingProgressionIndex > 0) {
        const [matchingElement] = sortedByRecency.splice(
          matchingProgressionIndex,
          1
        );
        sortedByRecency.unshift(matchingElement);
      }
    }
    return keepFlowAtHead(sortedByRecency);
  }

  private buildDisplayedList(): DisplayElement[] {
    // Compose final rendered rows from group heads, expansion state, and claims.
    const output: DisplayElement[] = [];
    const topLevelElements = this.getSortedTopLevelElements();
    topLevelElements.forEach((element) => {
      if (element.leadType === LeadType.CLAIM) {
        output.push(element);
        return;
      }
      const groupId = flowGroupKey(element);
      const group = this.flowGroupsByDisplayId[groupId];
      if (!group?.length) {
        return;
      }
      output.push(group[0]);
      if (this.expandedFlowGroupIds.has(groupId)) {
        output.push(...group.slice(1));
      }
    });
    return output;
  }

  private getSortedTopLevelElements(): DisplayElement[] {
    // Sorts group heads + claims with existing list sort settings.
    const flowHeads = Object.values(this.flowGroupsByDisplayId)
      .filter((group) => group.length > 0)
      .map((group) => group[0]);
    const claims = Object.values(this.claimById);
    const combined = [...flowHeads, ...claims];
    const sortMethod = getSortMethod(this._sortBy);
    combined.sort(sortMethod);
    if (this.ascending) {
      combined.reverse();
    }
    return combined;
  }

  private getElementTimestamp(element: DisplayElement): number {
    return new Date(element.lastUpdateDate ?? element.creationDate).getTime();
  }

  getRowId = (params: { data: DisplayElement }): string => params.data.id;
}
